/*
 * Caplansky's — box builder (export screen 4).
 *
 * Ours on purpose. No bundle app, no vendor SDK, no build step. Everything
 * about the catalogue comes from data attributes written by the section from
 * its settings, so the same asset serves the US store, the Canada market and
 * the .ca store without a line of it changing. Nothing here knows a variant id.
 *
 * Progressive enhancement: the markup is a real <form> posting to /cart/add
 * with one hidden id + quantity pair per flavour, so it still works with this
 * file blocked. JS takes over the quantity inputs, enforces the box size and
 * swaps the post for a fetch.
 *
 * State mirrors the design's documented model exactly:
 *   boxSize   2 | 4, changing it RESETS counts
 *   counts    per flavour, add is a no-op at total >= boxSize, remove at 0
 *   complete  total === boxSize, gates the CTA
 */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-cap-bb]');
  if (!roots.length) return;

  Array.prototype.forEach.call(roots, function (root) {
    if (root.dataset.capBbBound === 'true') return;
    root.dataset.capBbBound = 'true';
    setup(root);
  });

  function setup(root) {
    var tierButtons = Array.prototype.slice.call(root.querySelectorAll('[data-tier]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-flavor]'));
    if (!tierButtons.length || !cards.length) return;

    var form = root.querySelector('[data-cap-bb-form]');
    var slotWrap = root.querySelector('[data-slots]');
    var bar = root.querySelector('[data-progress-bar]');
    var statusEl = root.querySelector('[data-status]');
    var priceEl = root.querySelector('[data-total]');
    var submitEl = root.querySelector('[data-submit]');
    var clearEl = root.querySelector('[data-clear]');
    var flashEl = root.querySelector('[data-flash]');
    var sticky = root.querySelector('[data-sticky]');
    var stickySize = root.querySelector('[data-sticky-size]');
    var stickyMeta = root.querySelector('[data-sticky-meta]');
    var stickyCta = root.querySelector('[data-sticky-cta]');
    var tallyEl = root.querySelector('[data-tally]');

    var confirmMs = toInt(root.dataset.confirmMs, 2600);
    var afterAdd = root.dataset.afterAdd || 'cart';
    var addUrl = root.dataset.cartAddUrl || '/cart/add';
    var cartUrl = root.dataset.cartUrl || '/cart';
    var currency = root.dataset.currency || 'USD';

    // Flavours, in the order the section rendered them. Slots fill in this order.
    var flavors = cards.map(function (card) {
      return {
        id: card.dataset.flavor,
        name: card.dataset.name || '',
        handle: card.dataset.handle || '',
        color: card.dataset.color || '#1E6DA8',
        variantId: card.dataset.variantId || '',
        price: toInt(card.dataset.unitPrice, 0),
        available: card.dataset.available !== 'false',
        img: card.dataset.img || '',
        card: card,
        badge: card.querySelector('[data-badge]'),
        addButtons: Array.prototype.slice.call(card.querySelectorAll('[data-add]')),
        removeButton: card.querySelector('[data-remove]'),
        qtyInput: form ? form.querySelector('[data-qty="' + card.dataset.flavor + '"]') : null
      };
    });

    var tiers = tierButtons.map(function (btn) {
      /* Advertised box price, in cents. Blank until a real Shopify discount
         exists -- see displayTotal(). */
      var adv = parseFloat(btn.dataset.tierPrice);
      return {
        size: toInt(btn.dataset.tier, 0),
        el: btn,
        price: isNaN(adv) ? null : Math.round(adv * 100)
      };
    }).filter(function (t) { return t.size > 0; });
    if (!tiers.length) return;

    var boxSize = toInt(root.dataset.defaultSize, 0) || tiers[tiers.length - 1].size;
    if (!tiers.some(function (t) { return t.size === boxSize; })) boxSize = tiers[0].size;

    var counts = {};
    var flashTimer = null;
    var busy = false;

    // The quantity fieldset is the no-JS path. Hide it now that we are driving
    // the same inputs from the steppers — hidden inputs still post, which keeps
    // the form usable if fetch is unavailable.
    var fallback = root.querySelector('[data-fallback]');
    if (fallback) fallback.hidden = true;

    reset();
    bind();
    applyPrefill();
    render();

    function reset() {
      flavors.forEach(function (f) { counts[f.id] = 0; });
    }

    /* Deep link from a ready-made pair on the collection page:
         /pages/box-builder?box=2&add=spicy,horseradish#offer
       The visitor lands with the tier chosen and both jars in the box, so the
       only thing left to do is add to cart.

       Tokens match a flavour by name OR product handle, compared loosely, so a
       link keeps working if a name_override is edited or the store's handles
       differ -- CA's are not the same as US's. An unrecognised token is ignored
       rather than failing: a half-filled box still beats an error, and the
       visitor can finish it by hand.

       add() enforces the box limit, so a link asking for more than the tier
       holds just fills it and stops. Repeats are allowed (add=spicy,spicy). */
    function applyPrefill() {
      var query = window.location.search;
      if (!query || query.length < 2) return;

      var q = {};
      query.slice(1).split('&').forEach(function (pair) {
        var bits = pair.split('=');
        if (!bits[0]) return;
        try {
          q[decodeURIComponent(bits[0]).toLowerCase()] = decodeURIComponent((bits[1] || '').replace(/\+/g, ' '));
        } catch (e) { /* malformed escape in a hand-edited URL -- skip it */ }
      });

      var wantSize = toInt(q.box, 0);
      if (wantSize && tiers.some(function (t) { return t.size === wantSize; })) boxSize = wantSize;

      if (!q.add) return;
      q.add.split(',').forEach(function (token) {
        var f = findFlavor(token);
        if (f) add(f.id);
      });
    }

    /* Loose key match: lowercase, and compare both hyphenated and squashed so
       "old-fashioned", "Old Fashioned" and "oldfashioned" are all the same key. */
    function findFlavor(token) {
      var want = norm(token);
      if (!want) return null;
      for (var i = 0; i < flavors.length; i++) {
        var f = flavors[i];
        if (norm(f.name) === want || norm(f.handle) === want) return f;
        /* handles carry a product suffix the link should not have to know */
        if (norm(f.handle).indexOf(want) === 0) return f;
      }
      return null;
    }

    function norm(value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    function total() {
      return flavors.reduce(function (sum, f) { return sum + counts[f.id]; }, 0);
    }

    function subtotal() {
      return flavors.reduce(function (sum, f) { return sum + counts[f.id] * f.price; }, 0);
    }

    function complete() {
      return total() === boxSize;
    }

    /* What the customer is shown. Defaults to the honest line-item sum, because
       that is what the cart charges -- the builder must never advertise a price
       Shopify will not honour.

       Once a cart-level discount exists in the admin, setting a tier's price
       makes a FULL box show the box price instead, so the ticket agrees with the
       tier button beside it rather than contradicting it. A part-filled box
       always shows the running sum: no discount has been earned yet. */
    function displayTotal() {
      if (complete()) {
        var t = activeTier();
        if (t && t.price !== null) return t.price;
      }
      return subtotal();
    }

    function activeTier() {
      for (var i = 0; i < tiers.length; i++) if (tiers[i].size === boxSize) return tiers[i];
      return null;
    }

    function bind() {
      tiers.forEach(function (t) {
        t.el.addEventListener('click', function () {
          if (t.size === boxSize) return;
          boxSize = t.size;
          reset();               // documented: changing size clears the box
          clearFlash();
          render();
        });
      });

      flavors.forEach(function (f) {
        f.addButtons.forEach(function (btn) {
          btn.addEventListener('click', function () { add(f.id); });
        });
        if (f.removeButton) {
          f.removeButton.addEventListener('click', function () { remove(f.id); });
        }
      });

      if (clearEl) {
        clearEl.addEventListener('click', function () {
          reset();
          clearFlash();
          render();
        });
      }

      if (form) form.addEventListener('submit', onSubmit);
    }

    function add(id) {
      var f = byId(id);
      if (!f || !f.available) return;
      if (total() >= boxSize) return;   // the limit EB never enforced
      counts[id] += 1;
      clearFlash();
      render();
    }

    function remove(id) {
      if (!counts[id]) return;
      counts[id] -= 1;
      clearFlash();
      render();
    }

    function byId(id) {
      for (var i = 0; i < flavors.length; i++) if (flavors[i].id === id) return flavors[i];
      return null;
    }

    function render() {
      var t = total();
      var full = t >= boxSize;
      var done = complete();
      var left = boxSize - t;

      tiers.forEach(function (tier) {
        var on = tier.size === boxSize;
        tier.el.classList.toggle('is-active', on);
        tier.el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      flavors.forEach(function (f) {
        var n = counts[f.id];
        f.card.classList.toggle('is-active', n > 0);
        f.card.classList.toggle('is-soldout', !f.available);
        if (f.badge) {
          f.badge.textContent = '×' + n;
          f.badge.hidden = n === 0;
        }
        if (f.removeButton) f.removeButton.hidden = n === 0;
        f.addButtons.forEach(function (btn) {
          var blocked = full || !f.available;
          btn.disabled = blocked;
          if (btn.hasAttribute('data-add-label')) {
            btn.textContent = !f.available
              ? btn.dataset.soldoutLabel || 'Sold out'
              : full
                ? btn.dataset.fullLabel || 'Box is full'
                : n > 0
                  ? btn.dataset.moreLabel || 'Add another'
                  : btn.dataset.addLabel || 'Add to box';
          }
        });
        if (f.qtyInput) f.qtyInput.value = String(n);
      });

      renderSlots();

      if (bar) bar.style.width = (boxSize ? Math.round((t / boxSize) * 100) : 0) + '%';
      if (bar) bar.classList.toggle('is-complete', done);

      if (statusEl) {
        statusEl.textContent = done
          ? statusEl.dataset.doneLabel || 'Packed · ready to ship'
          : left + ' jar' + (left === 1 ? '' : 's') + ' to go';
        statusEl.classList.toggle('is-complete', done);
      }

      if (priceEl) priceEl.textContent = money(displayTotal());

      if (tallyEl) {
        var base = tallyEl.dataset.label || '';
        tallyEl.textContent = (base ? base + ' · ' : '') + t + '/' + boxSize + ' packed';
      }

      if (submitEl) {
        submitEl.disabled = !done || busy;
        submitEl.textContent = busy
          ? submitEl.dataset.busyLabel || 'Adding…'
          : done
            ? (submitEl.dataset.readyLabel || 'Add box to cart') + ' — ' + money(displayTotal())
            : 'Pick ' + left + ' more';
      }

      if (sticky) {
        sticky.classList.toggle('is-visible', t > 0);
        if (stickySize) stickySize.textContent = (stickySize.dataset.prefix || 'Box of') + ' ' + boxSize;
        if (stickyMeta) {
          stickyMeta.textContent = (done ? 'Packed and ready' : left + ' jar' + (left === 1 ? '' : 's') + ' to go') +
            ' · ' + money(displayTotal());
        }
        if (stickyCta) {
          stickyCta.textContent = done
            ? (stickyCta.dataset.readyLabel || 'Add to cart') + ' — ' + money(displayTotal())
            : stickyCta.dataset.label || 'Finish your box';
        }
      }
    }

    function renderSlots() {
      if (!slotWrap) return;
      slotWrap.style.setProperty('--cap-bb-slots', String(boxSize));
      slotWrap.innerHTML = '';

      var filled = [];
      flavors.forEach(function (f) {
        for (var i = 0; i < counts[f.id]; i++) filled.push(f);
      });

      for (var i = 0; i < boxSize; i++) {
        var f = filled[i] || null;
        var cell;
        if (f) {
          cell = document.createElement('button');
          cell.type = 'button';
          cell.className = 'cap-bb__slot is-filled';
          cell.style.setProperty('--slot-color', f.color);
          cell.title = 'Remove ' + f.name;
          cell.setAttribute('aria-label', 'Remove ' + f.name + ' from your box');
          if (f.img) {
            var img = document.createElement('img');
            img.src = f.img;
            img.alt = f.name;
            img.loading = 'lazy';
            cell.appendChild(img);
          } else {
            cell.textContent = f.name;
          }
          (function (id) {
            cell.addEventListener('click', function () { remove(id); });
          })(f.id);
        } else {
          cell = document.createElement('span');
          cell.className = 'cap-bb__slot';
          cell.textContent = pad(i + 1);
        }
        slotWrap.appendChild(cell);
      }
    }

    function onSubmit(event) {
      // No fetch support, or an incomplete box: let the browser post the form.
      if (!window.fetch) return;
      event.preventDefault();
      if (busy || !complete()) return;

      var items = flavors
        .filter(function (f) { return counts[f.id] > 0 && f.variantId; })
        .map(function (f) { return { id: f.variantId, quantity: counts[f.id] }; });
      if (!items.length) return;

      busy = true;
      render();

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (body) { throw new Error(body.description || body.message || 'Cart error'); });
          return res.json();
        })
        .then(function () {
          busy = false;
          if (afterAdd === 'cart') {
            window.location.href = cartUrl;
            return;
          }
          flash(flashEl && flashEl.dataset.doneLabel
            ? flashEl.dataset.doneLabel.replace('[count]', String(boxSize))
            : 'Added to cart · ' + boxSize + ' jars', false);
          reset();
          render();
        })
        .catch(function (err) {
          busy = false;
          flash(err && err.message ? err.message : 'Something went wrong. Try again.', true);
          render();
        });
    }

    function flash(message, isError) {
      if (!flashEl) return;
      flashEl.textContent = message;
      flashEl.hidden = false;
      flashEl.classList.toggle('is-error', !!isError);
      if (flashTimer) clearTimeout(flashTimer);
      flashTimer = setTimeout(clearFlash, confirmMs);
    }

    function clearFlash() {
      if (flashTimer) { clearTimeout(flashTimer); flashTimer = null; }
      if (!flashEl) return;
      flashEl.hidden = true;
      flashEl.textContent = '';
      flashEl.classList.remove('is-error');
    }

    function money(cents) {
      var amount = cents / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2
        }).format(amount);
      } catch (e) {
        return '$' + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2));
      }
    }

    function pad(n) { return n < 10 ? '0' + n : String(n); }
    function toInt(v, fallback) { var n = parseInt(v, 10); return isNaN(n) ? fallback : n; }
  }
})();
